import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, REPORT_TYPES, reportLabel, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { DashboardSkeleton } from "@/components/Skeleton";
import DecisionBadge, { decisionState } from "@/components/DecisionBadge";
import StageBadge from "@/components/StageBadge";
import { householdStageFor, stageFor } from "@/lib/verifyHub";
import { useUnreadIndex, NOTIFICATION_BADGE_LABEL, NOTIFICATION_BADGE_TONE } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowRight, Building2, ChevronDown, Layers, ListFilter, Search, FileText, Users, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Merge as MergeIcon, Check as CheckIcon, X as CloseIcon, Archive, ArchiveRestore, Inbox, Briefcase, ListChecks, BadgeCheck, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import VerificationPipeline from "@/components/VerificationPipeline";
import DashboardPriorityRow from "@/components/DashboardPriorityRow";
import { fmtDate } from "@/lib/dateFormat";

// Human labels for the stage-filter chip. Stage keys match
// /app/frontend/src/lib/verifyHub.js PIPELINE_STAGES + the rollup
// "in_verification" used by the hero card.
const STAGE_FILTER_LABEL = {
  submitted:       "Application Submitted",
  verify:          "Screening & Reports",
  validation:      "Verification & Validation",
  additional:      "Additional Information Needed",
  ready:           "Decision-Ready Review",
  complete:        "Completed Applications",
  in_verification: "Screening + Verification & Validation",
};

function StatCard({ label, value, tone = "neutral", Icon, hint, testid, onClick, active }) {
  // Tone palette matches LandingPage.jsx PIPELINE_DOT — keeps dashboard 1:1
  // with the public marketing visualization.
  const TONE = {
    neutral: { dot: "bg-slate-300",   value: "text-slate-900", iconBg: "bg-slate-100",  iconFg: "text-slate-600", ring: "ring-slate-300" },
    active:  { dot: "bg-blue-500",    value: "text-slate-900", iconBg: "bg-blue-50",    iconFg: "text-blue-600",  ring: "ring-blue-500" },
    ready:   { dot: "bg-violet-500",  value: "text-slate-900", iconBg: "bg-violet-50",  iconFg: "text-violet-600", ring: "ring-violet-500" },
    complete:{ dot: "bg-emerald-500", value: "text-slate-900", iconBg: "bg-emerald-50", iconFg: "text-emerald-600", ring: "ring-emerald-500" },
  }[tone] || {};
  const clickable = !!onClick;
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={`w-full text-left bg-white rounded-xl p-5 shadow-sm transition-all
        ${active
          ? `ring-2 ${TONE.ring} shadow-md -translate-y-0.5`
          : "ring-1 ring-slate-200 hover:shadow-md hover:-translate-y-0.5"}
        ${clickable ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" : ""}`}
      data-testid={testid || `stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      data-active={active ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="ntn-eyebrow flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${TONE.dot}`} />
            {label}
          </div>
          <div className={`ntn-metric mt-2 ${TONE.value}`}>{value}</div>
          {hint && <div className="ntn-caption mt-2">{hint}</div>}
        </div>
        {Icon && (
          <div className={`h-9 w-9 rounded-lg ${TONE.iconBg} flex items-center justify-center shrink-0`}>
            <Icon className={`h-4 w-4 ${TONE.iconFg}`} strokeWidth={1.7} />
          </div>
        )}
      </div>
    </Tag>
  );
}

function ScreeningRow({ s, selectable, selected, onToggleSelect, onArchiveToggle, unreadTypes }) {
  const state = decisionState(s);
  const inGroup = !!s.group_id;
  const archived = !!s.archived_at;
  const ageMs = Date.now() - new Date(s.created_at).getTime();
  const isNew = !archived && ageMs < 24 * 60 * 60 * 1000;
  // Deduplicate notification types
  const badgeTypes = Array.from(new Set(unreadTypes || [])).slice(0, 2);

  const archiveBtn = onArchiveToggle && !selectable ? (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onArchiveToggle(s); }}
      title={archived ? "Restore" : "Archive"}
      data-testid={`row-archive-${s.id}`}
      className={`h-8 w-8 rounded-sm flex items-center justify-center transition-all
        ${archived
          ? "text-[#15803D] hover:bg-[#22C55E]/15"
          : "text-[#4A5568] opacity-0 group-hover:opacity-100 hover:bg-[#F3EFEA] hover:text-[#0A192F]"}`}
    >
      {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
    </button>
  ) : null;

  const content = (
    <div className="grid grid-cols-12 gap-4 items-center">
      {selectable && (
        <div className="col-span-12 sm:col-span-1 -mr-2 flex items-center" onClick={(e) => e.stopPropagation()}>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={selected}
              disabled={inGroup}
              onCheckedChange={() => onToggleSelect?.(s.id)}
              data-testid={`screening-select-${s.id}`}
              aria-label={`Select ${s.applicant_first_name} ${s.applicant_last_name}`}
            />
            {inGroup && (
              <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#4A5568]">
                in group
              </span>
            )}
          </label>
        </div>
      )}
      <div className={selectable ? "col-span-12 sm:col-span-2" : "col-span-3"}>
        <div className="font-semibold text-[#0A192F] truncate flex items-center gap-2 flex-wrap">
          <span className="truncate">{s.applicant_first_name} {s.applicant_last_name}</span>
          {badgeTypes.map((t) => (
            <span key={t}
              className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm shrink-0 ${NOTIFICATION_BADGE_TONE[t] || "bg-[#0A192F] text-white"}`}
              data-testid={`row-notif-${t}-${s.id}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
              {NOTIFICATION_BADGE_LABEL[t] || "UPDATE"}
            </span>
          ))}
          {isNew && badgeTypes.length === 0 && (
            <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.14em] text-white bg-[#F59E0B] px-1.5 py-0.5 rounded-sm shrink-0"
                  data-testid={`row-new-badge-${s.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
              NEW
            </span>
          )}
          {archived && !selectable && (
            <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#4A5568] bg-[#F3EFEA] px-1.5 py-0.5 rounded-sm">archived</span>
          )}
        </div>
        <div className="text-[11px] text-[#4A5568] font-mono truncate">{s.applicant_email}</div>
      </div>
      <div className="col-span-3 hidden md:block">
        <div className="text-sm text-[#0A192F] truncate">{s.property_address}</div>
        <div className="text-[11px] text-[#4A5568]">{s.monthly_rent ? `$${Number(s.monthly_rent).toLocaleString()}/mo` : "—"}</div>
      </div>
      <div className="col-span-2 hidden lg:flex flex-wrap gap-1">
        {(s.services || []).slice(0, 3).map((svc) => (
          <Badge key={svc} variant="outline" className="text-[10px] rounded-sm font-normal border-[#0A192F]/15 text-[#4A5568]">{reportLabel(svc).split(" ")[0]}</Badge>
        ))}
        {s.services?.length > 3 && (
          <Badge variant="outline" className="text-[10px] rounded-sm font-normal">+{s.services.length - 3}</Badge>
        )}
      </div>
      <div className="col-span-2 text-[11px] font-mono text-[#4A5568] hidden sm:block">{fmtDate(s.created_at)}</div>
      <div className="col-span-9 sm:col-span-7 md:col-span-4 lg:col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
        <StageBadge screening={s} size="xs" showIcon={false} testid={`row-stage-${s.id}`} />
        <DecisionBadge state={state} pulse />
        {archiveBtn}
        {!selectable && <ArrowRight className="h-4 w-4 text-[#4A5568] group-hover:translate-x-0.5 group-hover:text-[#0A192F] transition-all hidden sm:block" />}
      </div>
    </div>
  );

  if (selectable) {
    return (
      <div data-testid={`screening-row-${s.id}`}
           className={`block border-b border-[#0A192F]/8 last:border-0 px-5 py-4 ${
             selected ? "bg-[#0A192F]/5" : inGroup ? "bg-[#F3EFEA]/30 opacity-60" : "hover:bg-[#F3EFEA]/30"
           } transition-colors`}>
        {content}
      </div>
    );
  }

  return (
    <Link to={`/screenings/${s.id}`} data-testid={`screening-row-${s.id}`}
      className="block ntn-row border-b border-[#0A192F]/8 last:border-0 px-5 py-4 group">
      {content}
    </Link>
  );
}


/**
 * SingleApplicantHouseholdCard — every standalone applicant on the dashboard
 * renders as a synthetic 1-member household per platform spec. Mirrors the
 * MemberCard visual language used on the Household Profile.
 */
const REC_PILL = {
  approve:     { label: "Recommended",                 cls: "bg-emerald-700 text-white" },
  conditional: { label: "Recommended with Conditions", cls: "bg-amber-600 text-white" },
  deny:        { label: "Not Recommended",             cls: "bg-rose-700 text-white" },
};

// PM Final Decision pill — closing outcome that supersedes any NTN
// Recommendation in the household card. Approved/Not Approved are non-neutral
// outcomes; Not Selected is intentionally neutral (slate, not red) per the
// platform spec — it is not a denial.
const FINAL_PILL = {
  approved:     { label: "Approved · Closed",     cls: "bg-emerald-700 text-white" },
  denied:       { label: "Not Approved · Closed", cls: "bg-rose-700 text-white" },
  not_selected: { label: "Not Selected · Closed", cls: "bg-slate-700 text-white" },
};

const RIS_PILL = {
  pending:     { label: "Pending NTN Review",                     cls: "bg-slate-50 text-slate-700 ring-slate-200" },
  none:        { label: "No Risk Indicators Found",               cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  potential:   { label: "Potential Risk Indicators Found",        cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  significant: { label: "Significant Risk Indicators Found",      cls: "bg-orange-50 text-orange-800 ring-orange-200" },
  elevated:    { label: "Elevated Risk Indicators Found",         cls: "bg-rose-50 text-rose-700 ring-rose-200" },
};

const RIS_SEV_RANK = { potential: 1, significant: 2, elevated: 3 };
const RIS_ACTIVE_STATUSES = new Set(["open", "under_review", "reviewed"]);

function risStateFor(screening) {
  // Phase E.1 (2026-02): pill reflects the manually-assigned applicant risk.
  // Falls back to "pending" when NTN staff hasn't chosen a value yet.
  const stored = screening?.applicant_risk_summary;
  if (stored && stored.severity) return stored.severity;
  return "pending";
}

function SingleApplicantHouseholdCard({ s, selectable, selected, onToggleSelect, onArchiveToggle, unreadTypes }) {
  const nav = useNavigate();
  const stage = stageFor(s);
  const open = (s.risk_indicators || []).filter((r) => ["open", "under_review", "reviewed"].includes(r.status)).length;
  const ris = RIS_PILL[risStateFor(s)] || RIS_PILL.pending;
  const finalPill = s.final_decision ? FINAL_PILL[s.final_decision] : null;
  const rec = !finalPill && s.recommendation ? REC_PILL[s.recommendation] : null;
  const householdName = `${s.applicant_last_name || s.applicant_first_name || "Single"} household`;

  return (
    <div className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm hover:shadow-md transition-all border-l-4 border-l-slate-200 hover:border-l-slate-900 p-4"
         data-testid={`household-card-single-${s.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        {selectable && (
          <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect?.(s.id)}
                 className="mt-1.5 h-4 w-4 rounded border-slate-300" data-testid={`select-${s.id}`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Household</div>
            <span className="text-slate-300">·</span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">1 applicant</span>
          </div>
          <h3 className="font-serif text-xl text-slate-900 leading-tight mt-1 truncate" data-testid={`household-name-${s.id}`}>
            {householdName}
          </h3>
          <div className="text-xs text-slate-500 mt-0.5 font-mono">
            {s.applicant_first_name} {s.applicant_last_name} · ID {s.id.slice(0, 8).toUpperCase()}
          </div>
          {(s.company || s.submitted_by_company) && (
            <div className="text-[11px] text-slate-600 mt-0.5" data-testid={`household-subscriber-${s.id}`}>
              <span className="uppercase tracking-[0.12em] text-[10px] text-slate-400 mr-1">Ordering Subscriber</span>
              <span className="font-medium">{s.company || s.submitted_by_company}</span>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {finalPill ? (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold ${finalPill.cls} shadow-sm`}
                    data-testid={`household-final-${s.id}`}>
                {finalPill.label}
              </span>
            ) : rec ? (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold ${rec.cls} shadow-sm`}
                    data-testid={`household-rec-${s.id}`}>
                {rec.label}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold text-slate-600 bg-slate-50 ring-1 ring-slate-200">
                Recommendation Pending
              </span>
            )}
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-inset ${ris.cls}`}
                  data-testid={`household-ris-${s.id}`}>
              {ris.label}
            </span>
            {open > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 ring-1 ring-amber-300">
                {open} open
              </span>
            )}
            <StageBadge stageKey={stage.key} />
            {unreadTypes && unreadTypes.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-900 ring-1 ring-blue-300">
                {unreadTypes.length} new
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onArchiveToggle && (
            <Button variant="outline" size="sm" className="h-9 rounded-md border-slate-300 text-slate-600"
                    onClick={(e) => { e.stopPropagation(); onArchiveToggle(s); }}
                    data-testid={`archive-${s.id}`}>
              {s.archived_at ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            </Button>
          )}
          <Button onClick={() => nav(`/screenings/${s.id}`)} className="bg-slate-900 hover:bg-slate-800 rounded-md h-9 px-4"
                  data-testid={`household-view-${s.id}`}>
            View Household <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function PropertyCard({ address, screenings, defaultOpen, expansionNonce, selectable, selectedIds, onToggleSelect, onArchiveToggle, groupsById, onArchiveGroup, unreadByScreening, unreadByGroup }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (expansionNonce !== undefined) setOpen(defaultOpen);
  }, [expansionNonce]);  // intentional: only re-sync on parent bump

  // Household-first counts per platform spec. Every standalone applicant
  // is counted as a 1-member household.
  const householdIds = new Set();
  screenings.forEach((s) => householdIds.add(s.group_id || `single:${s.id}`));
  const householdCount = householdIds.size;
  const openRiCount = screenings.reduce((acc, s) => acc + ((s.risk_indicators || []).filter((r) => ["open", "under_review", "reviewed"].includes(r.status)).length), 0);
  const decisionReadyHouseholds = screenings.filter((s) => ["ready", "approve", "conditional", "deny"].includes(decisionState(s))).length;

  // Split "123 Main Street, Roseville, CA" → street + locality so the property
  // header reads like a building label rather than a single dense line.
  const [streetLine, ...localityParts] = (address || "—").split(",").map((p) => p.trim()).filter(Boolean);
  const locality = localityParts.join(", ");

  // Bucket screenings: ungrouped first (newest first), then groups by latest member
  const buckets = useMemo(() => {
    const ungrouped = [];
    const groupedMap = {}; // gid -> screenings[]
    screenings.forEach((s) => {
      if (s.group_id) {
        (groupedMap[s.group_id] = groupedMap[s.group_id] || []).push(s);
      } else {
        ungrouped.push(s);
      }
    });
    ungrouped.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const groupBuckets = Object.entries(groupedMap).map(([gid, list]) => {
      list.sort((a, b) => {
        if (a.group_role === "primary" && b.group_role !== "primary") return -1;
        if (b.group_role === "primary" && a.group_role !== "primary") return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      const latest = list.reduce((m, s) => Math.max(m, new Date(s.created_at).getTime()), 0);
      return { gid, list, latest };
    }).sort((a, b) => b.latest - a.latest);
    return { ungrouped, groupBuckets };
  }, [screenings]);

  return (
    <div
      className={`group/property relative bg-white rounded-lg ring-1 ring-slate-200 shadow-sm hover:shadow-md transition-all border-l-4 border-l-[#0A192F] overflow-hidden ${open ? "" : "ring-slate-200/60"}`}
      data-testid={`property-card-${address}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left px-6 py-5 flex items-center gap-5 transition-colors ${open ? "bg-gradient-to-r from-slate-50 to-white" : "hover:bg-slate-50"}`}
        data-testid={`property-toggle-${address}`}
      >
        <div className="h-14 w-14 rounded-md bg-[#0A192F] flex items-center justify-center shrink-0 shadow-md">
          <Building2 className="h-7 w-7 text-amber-300" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">Property</div>
          <div className="font-serif text-2xl text-slate-900 leading-tight truncate" data-testid={`property-card-street-${address}`}>
            {streetLine || "—"}
          </div>
          {locality && (
            <div className="text-sm text-slate-600 mt-0.5 truncate" data-testid={`property-card-locality-${address}`}>
              {locality}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-1.5 font-medium flex items-center gap-1.5" data-testid={`property-card-count-${address}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            {householdCount} household{householdCount !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          {openRiCount > 0 && (
            <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider font-bold border-amber-200 bg-amber-50 text-amber-800 px-2.5 py-1" data-testid={`property-open-ri-${address}`}>
              {openRiCount} risk indicator{openRiCount !== 1 ? "s" : ""}
            </Badge>
          )}
          {decisionReadyHouseholds > 0 && (
            <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider font-bold border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1" data-testid={`property-decision-ready-${address}`}>
              {decisionReadyHouseholds} decision-ready
            </Badge>
          )}
        </div>
        <div className={`h-9 w-9 rounded-full ring-1 ring-slate-200 flex items-center justify-center transition-all ${open ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-600 group-hover/property:bg-slate-100"}`}>
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 ntn-fade-in p-4 space-y-3 bg-slate-50/30">
          {/* Every standalone applicant renders as a synthetic 1-member
              household per platform spec — never as a bare applicant row. */}
          {buckets.ungrouped.map((s) => (
            <SingleApplicantHouseholdCard key={s.id} s={s}
              selectable={selectable}
              selected={selectedIds?.has(s.id)}
              onToggleSelect={onToggleSelect}
              onArchiveToggle={onArchiveToggle}
              unreadTypes={unreadByScreening?.[s.id]} />
          ))}
          {buckets.groupBuckets.map(({ gid, list }) => {
            const g = groupsById?.[gid];
            const groupName = (g?.name?.trim()) || `${list[0]?.applicant_last_name || ""} group`.trim() || "Group";
            return (
              <GroupBucket key={gid} gid={gid} group={g} groupName={groupName} list={list}
                selectable={selectable} selectedIds={selectedIds}
                onToggleSelect={onToggleSelect} onArchiveToggle={onArchiveToggle}
                onArchiveGroup={onArchiveGroup}
                unreadByScreening={unreadByScreening}
                unreadByGroup={unreadByGroup} />
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupBucket({ gid, group, groupName, list, selectable, selectedIds, onToggleSelect, onArchiveToggle, onArchiveGroup, unreadByScreening, unreadByGroup }) {
  const archived = !!group?.archived_at;
  const verdict = group?.recommendation || group?.household?.suggested_verdict;
  const groupUnreadTypes = unreadByGroup?.[gid] || [];
  const verdictCfg = {
    approve:     { tone: "border-[#22C55E]/40 bg-[#22C55E]/8 text-[#15803D]",  label: "APPROVE" },
    conditional: { tone: "border-[#F59E0B]/40 bg-[#F59E0B]/8 text-[#92400E]",  label: "CONDITIONAL" },
    deny:        { tone: "border-[#EF4444]/40 bg-[#EF4444]/8 text-[#B91C1C]",  label: "DENY" },
  }[verdict];

  return (
    <div className="border-t border-[#0A192F]/8 bg-[#F3EFEA]/20" data-testid={`property-group-${gid}`}>
      <div className="flex items-center gap-3 px-5 py-2.5 border-b border-[#0A192F]/8 bg-[#F3EFEA]/40">
        <Users className="h-3.5 w-3.5 text-[#4A5568] shrink-0" />
        <Link to={`/groups/${gid}`}
          className="text-[11px] font-mono tracking-[0.14em] uppercase text-[#0A192F] font-bold hover:underline truncate"
          data-testid={`property-group-link-${gid}`}>
          {groupName}
        </Link>
        <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#4A5568]">
          · {list.length} applicant{list.length !== 1 ? "s" : ""}
        </span>
        {verdictCfg && (
          <span className={`text-[9px] font-mono font-bold tracking-[0.14em] px-1.5 py-0.5 rounded-sm border ${verdictCfg.tone}`}
                data-testid={`property-group-verdict-${gid}`}>
            {verdictCfg.label}
          </span>
        )}
        {groupUnreadTypes.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.14em] text-white bg-[#EF4444] px-1.5 py-0.5 rounded-sm"
                data-testid={`property-group-unread-${gid}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            UPDATED
          </span>
        )}
        {archived && (
          <span className="text-[9px] font-mono tracking-[0.14em] uppercase text-[#4A5568] bg-white border border-[#0A192F]/15 px-1.5 py-0.5 rounded-sm">
            archived
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Link to={`/groups/${gid}`}
            className="text-[10px] font-mono tracking-[0.14em] uppercase text-[#0A192F] hover:underline"
            data-testid={`property-group-open-${gid}`}>
            Open household →
          </Link>
          {onArchiveGroup && group && !selectable && (
            <button type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onArchiveGroup(group); }}
              title={archived ? "Restore household" : "Archive household"}
              data-testid={`property-group-archive-${gid}`}
              className={`h-6 w-6 rounded-sm flex items-center justify-center transition-colors
                ${archived
                  ? "text-[#15803D] hover:bg-[#22C55E]/15"
                  : "text-[#4A5568] hover:bg-white hover:text-[#0A192F]"}`}>
              {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
      {list.map((s) => (
        <ScreeningRow key={s.id} s={s}
          selectable={selectable}
          selected={selectedIds?.has(s.id)}
          onToggleSelect={onToggleSelect}
          onArchiveToggle={onArchiveToggle}
          unreadTypes={unreadByScreening?.[s.id]} />
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { user, hasPermission, reportScope, companyLabel } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, submitted: 0, in_progress: 0, complete: 0 });
  const [items, setItems] = useState([]);
  const [view, setView] = useState("by_property");
  const [filter, setFilter] = useState("all");
  // Stage filter — drives clickable hero cards + pipeline rows. `null` = no filter.
  // Value is a stage `key` from PIPELINE_STAGES, or one of the rollup keys
  // 'in_verification' (verify+validation) / 'all' (clears).
  const [stageFilter, setStageFilter] = useState(null);
  // Risk Indicator filter — driven by the Risk Intelligence callout cards.
  //   'any' (default, no filter) | 'with_indicators' (≥1 indicator) |
  //   'requiring_review' (≥1 indicator with status open|under_review)
  const [riskFilter, setRiskFilter] = useState("any");
  const [search, setSearch] = useState("");
  const [subscriberFilter, setSubscriberFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // Closed Applications Archive — filter by PM Final Decision outcome.
  // 'all' (no filter) | 'approved' | 'denied' | 'not_selected'.
  const [finalDecisionFilter, setFinalDecisionFilter] = useState("all");
  const [groups, setGroups] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  // Loading gate — true until the first stats+items fetch settles. Drives
  // the DashboardSkeleton (P1 audit follow-up: no more flash of empty
  // dashboard on slow networks).
  const [initialLoading, setInitialLoading] = useState(true);
  // Property expansion control — null = use defaultOpen (top-3 auto-open).
  // Expand/Collapse all bumps expansionNonce so PropertyCards re-sync.
  const [propertyDefaultOpen, setPropertyDefaultOpenState] = useState(null);
  const [expansionNonce, setExpansionNonce] = useState(0);
  const setPropertyExpansion = (next) => {
    setPropertyDefaultOpenState(next);
    setExpansionNonce((n) => n + 1);
  };
  const canMerge = user?.role === "property_manager" || user?.role === "ntn_staff";
  const { unreadByScreening, unreadByGroup } = useUnreadIndex();

  const reload = () => {
    const statsP = apiClient.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    // v1.0.7 — fetch EVERY page (backend caps a single page at 500) so stage
    // filters/counts cover the full pipeline, not just the first 100 records.
    const fetchAllPages = async (path, extra = "") => {
      const out = [];
      const limit = 500;
      let offset = 0;
      for (;;) {
        const { data } = await apiClient.get(`${path}?limit=${limit}&offset=${offset}${extra}`);
        out.push(...(data || []));
        if (!data || data.length < limit) break;
        offset += limit;
      }
      return out;
    };
    const itemsP = fetchAllPages("/screenings", showArchived ? "&archived=true" : "")
      .then(setItems).catch(() => {});
    let groupsP = Promise.resolve();
    if (user?.role !== "applicant") {
      groupsP = fetchAllPages("/groups", showArchived ? "&archived=true" : "")
        .then(setGroups).catch(() => setGroups([]));
    }
    Promise.all([statsP, itemsP, groupsP]).finally(() => setInitialLoading(false));
  };

  useEffect(() => { reload(); }, [showArchived, user]); // eslint-disable-line

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  const selectedScreenings = useMemo(
    () => items.filter((s) => selectedIds.has(s.id) && !s.group_id),
    [items, selectedIds],
  );

  const onArchiveGroup = async (g) => {
    const archived = !!g.archived_at;
    const path = archived ? "unarchive" : "archive";
    try {
      await apiClient.post(`/groups/${g.id}/${path}`);
      toast.success(archived ? "Household restored" : "Household archived");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  const onArchiveToggle = async (s) => {
    const archived = !!s.archived_at;
    const path = archived ? "unarchive" : "archive";
    try {
      await apiClient.post(`/screenings/${s.id}/${path}`);
      setItems((arr) => arr.map((x) => x.id === s.id
        ? { ...x, archived_at: archived ? null : new Date().toISOString() } : x));
      toast.success(archived ? "Restored" : "Archived");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  // Manual archive control is reserved for NTN staff (administrative
  // override). Property Managers close screenings exclusively through the
  // "Record Final Decision" flow on the screening detail page, which
  // auto-archives. Hiding the row-level archive button enforces this.
  const canManualArchive = user?.role === "ntn_staff";
  const rowArchiveToggle = canManualArchive ? onArchiveToggle : null;
  const groupArchiveToggle = canManualArchive ? onArchiveGroup : null;

  const bulkArchive = async () => {
    const ids = selectedScreenings
      .filter((s) => !s.archived_at)
      .map((s) => s.id);
    if (ids.length === 0) {
      toast.message("Nothing to archive — all selected are already archived.");
      return;
    }
    try {
      const { data } = await apiClient.post(`/screenings/bulk-archive`, { ids });
      toast.success(`Archived ${data.archived} screening${data.archived !== 1 ? "s" : ""}`);
      exitSelectMode();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk archive failed");
    }
  };

  const bulkRestore = async () => {
    const ids = selectedScreenings
      .filter((s) => !!s.archived_at)
      .map((s) => s.id);
    if (ids.length === 0) {
      toast.message("Nothing to restore — none of the selected are archived.");
      return;
    }
    try {
      const { data } = await apiClient.post(`/screenings/bulk-restore`, { ids });
      toast.success(`Restored ${data.restored} screening${data.restored !== 1 ? "s" : ""}`);
      exitSelectMode();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk restore failed");
    }
  };

  const bulkPermanentDelete = async (reason) => {
    const ids = selectedScreenings.map((s) => s.id);
    if (ids.length === 0) return;
    try {
      const { data } = await apiClient.post(`/admin/screenings/bulk-permanent-delete`, {
        ids, confirm: "DELETE", reason,
      });
      toast.success(`Permanently deleted ${data.deleted} applicant${data.deleted !== 1 ? "s" : ""}`);
      exitSelectMode();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  // Final-decision state keys produced by decisionState() when s.final_decision is set.
  const FINAL_DECISION_STATES = ["final_approved", "final_denied", "final_not_selected"];
  // Closing-decision states (NTN Recommendation + PM Final Decision) — both
  // count as "Decided" for the active-list filter chip.
  const DECIDED_STATES = ["approve", "conditional", "deny", ...FINAL_DECISION_STATES];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Map stage filter keys -> set of verify_status values to match against.
    // Keep in sync with /app/frontend/src/lib/verifyHub.js PIPELINE_STAGES.
    const STAGE_FILTER_MAP = {
      submitted:   ["application_received", "intake", null, ""],
      verify:      ["verification_in_progress"],
      validation:  ["validation_in_progress"],
      additional:  ["additional_information_requested"],
      ready:       ["ready_for_processing"],
      complete:    ["complete"],
      // Rollups used by the hero cards
      in_verification: ["verification_in_progress", "validation_in_progress"],
    };
    return items.filter((s) => {
      if (filter !== "all") {
        const state = decisionState(s);
        if (filter === "open" && DECIDED_STATES.includes(state)) return false;
        if (filter === "decided" && !DECIDED_STATES.includes(state)) return false;
        if (filter === "awaiting" && state !== "awaiting") return false;
      }
      // Archive view: optional filter by Final Decision outcome.
      if (showArchived && finalDecisionFilter && finalDecisionFilter !== "all") {
        if (s.final_decision !== finalDecisionFilter) return false;
      }
      if (stageFilter && stageFilter !== "all") {
        const allowed = STAGE_FILTER_MAP[stageFilter];
        if (allowed) {
          const vs = s.workflow_stage ?? s.verify_status ?? null;
          if (!allowed.includes(vs)) return false;
        }
      }
      if (riskFilter && riskFilter !== "any") {
        const indicators = s.risk_indicators || [];
        if (riskFilter === "with_indicators" && indicators.length === 0) return false;
        if (riskFilter === "requiring_review" && !indicators.some((r) => r.status === "open" || r.status === "under_review")) return false;
      }
      if (subscriberFilter.trim()) {
        const sub = `${s.company || ""} ${s.submitted_by_company || ""} ${s.company_id || ""}`.toLowerCase();
        if (!sub.includes(subscriberFilter.trim().toLowerCase())) return false;
      }
      if (!q) return true;
      const hay = [s.applicant_first_name, s.applicant_last_name, s.applicant_email, s.property_address,
                   s.company, s.submitted_by_company].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter, search, stageFilter, riskFilter, showArchived, finalDecisionFilter, subscriberFilter]);

  const byProperty = useMemo(() => {
    const groupsByAddr = {};
    filtered.forEach((s) => {
      const key = s.property_address || "—";
      if (!groupsByAddr[key]) groupsByAddr[key] = [];
      groupsByAddr[key].push(s);
    });
    // Sort properties by the most recent screening so new requests rise to the top
    return Object.entries(groupsByAddr).map(([address, list]) => ({
      address,
      list,
      latestAt: list.reduce((max, s) => {
        const t = new Date(s.created_at).getTime();
        return t > max ? t : max;
      }, 0),
    })).sort((a, b) => b.latestAt - a.latestAt);
  }, [filtered]);

  // Index groups by id for inline rendering inside PropertyCard
  const groupsById = useMemo(() => {
    const m = {};
    (groups || []).forEach((g) => { m[g.id] = g; });
    return m;
  }, [groups]);

  const isStaff = user?.role === "ntn_staff";

  if (initialLoading) {
    return (
      <AppShell>
        <DashboardSkeleton />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 ntn-fade-up">
        <div>
          <div className="ntn-eyebrow mb-3 flex items-center gap-3 flex-wrap" data-testid="dashboard-eyebrow">
            <span>{isStaff ? "Staff console" : user?.role === "applicant" ? "Applicant view" : "Property manager"}</span>
            {user?.company_account_number && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#F3EFEA] text-[#0A192F] ntn-mono normal-case"
                    data-testid="dashboard-account-number">
                ACCT #{user.company_account_number}
              </span>
            )}
          </div>
          {/* Phase E.4: primary heading = company branding; scope becomes a
              secondary chip so authorization scope is not confused with brand. */}
          {isStaff ? (
            <h1 className="ntn-h1" data-testid="dashboard-heading">Application Pipeline</h1>
          ) : (
            <div>
              {companyLabel && (
                <div className="ntn-eyebrow text-[#4A5568]" data-testid="dashboard-company-brand">
                  {companyLabel}
                </div>
              )}
              <h1 className="ntn-h1 mt-1" data-testid="dashboard-heading">Applications</h1>
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-[#F3EFEA] px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#4A5568]"
                   data-testid="dashboard-scope-chip">
                <span>Viewing:</span>
                <span className="text-[#0A192F]">
                  {reportScope === "self" ? "My Applications"
                    : reportScope === "team" ? "Team Applications"
                    : "Company Applications"}
                </span>
              </div>
            </div>
          )}
          <p className="ntn-body mt-2" style={{ color: "#4A5568" }}>
            {isStaff
              ? "Review incoming requests, update verification statuses, and respond to property managers."
              : (
                  reportScope === "self" ? "Applications you submitted. Track decisions. Move applicants in faster." :
                  reportScope === "team" ? "Applications submitted by your team. Track decisions across your leasing group." :
                  "Applications submitted across your entire company."
                )}
          </p>
        </div>
        {/* Phase D: New application CTA gated by reports.order permission. */}
        {(user?.role === "ntn_staff" || hasPermission?.("reports.order")) && (
          <div className="flex gap-2">
            <Link to="/screenings/new" data-testid="dashboard-new-request">
              <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-sm h-11 px-5">
                <Plus className="mr-2 h-4 w-4" /> New Request
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Priority Cards Row — Command Center for PMs and Staff */}
      {(user?.role === "property_manager" || isStaff) && stats.applications_submitted > 0 && (
        <div className="ntn-fade-up" style={{ animationDelay: "0.03s" }}>
          <DashboardPriorityRow
            isStaff={isStaff}
            stats={stats}
            onStageFilter={(key) => setStageFilter((cur) => cur === key ? null : key)}
            onRiskFilter={(key) => setRiskFilter((cur) => cur === key ? "any" : key)}
          />
        </div>
      )}

      {stats.pipeline && stats.pipeline.some((s) => s.count > 0) && (
        <div className="mb-6 ntn-fade-up" style={{ animationDelay: "0.05s" }}>
          <VerificationPipeline
            stages={stats.pipeline}
            activeStageKey={stageFilter && stageFilter !== "all" && stageFilter !== "in_verification" ? stageFilter : null}
            onStageClick={(key) => setStageFilter((cur) => cur === key ? null : key)}
            title={isStaff ? "Active applicants across all clients" : "Where your applicants stand right now"}
            subtitle={`${stats.applications_submitted ?? stats.total ?? 0} total · live`}
            testid="dashboard-pipeline"
          />
        </div>
      )}

      {(stats.applications_with_risk_indicators > 0 || (isStaff && stats.risk_indicators_requiring_review > 0)) && (
        <div className="mb-6 ntn-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="bg-white rounded-xl ring-1 ring-amber-200 shadow-sm overflow-hidden" data-testid="risk-intelligence-callout">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="ntn-eyebrow text-amber-700">Risk Intelligence</div>
                <div className="ntn-h3 mt-0.5">
                  {isStaff ? "Indicators across the queue" : "Applications flagged with Risk Indicators"}
                </div>
              </div>
              <div className="ntn-caption text-amber-700 italic">Click a metric to filter the list below</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-amber-100">
              <button
                type="button"
                onClick={() => setRiskFilter((cur) => cur === "with_indicators" ? "any" : "with_indicators")}
                className={`text-left p-5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                  riskFilter === "with_indicators" ? "bg-amber-50 ring-2 ring-amber-400" : "hover:bg-amber-50/40 cursor-pointer"
                }`}
                data-testid="stat-applications-with-risk-indicators-btn"
                data-active={riskFilter === "with_indicators" ? "true" : "false"}
              >
                <div className="ntn-eyebrow flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Applications
                </div>
                <div className="ntn-metric-sm mt-2" data-testid="stat-applications-with-risk-indicators">{stats.applications_with_risk_indicators ?? 0}</div>
                <div className="ntn-caption mt-1.5">With Risk Indicators</div>
              </button>
              <button
                type="button"
                onClick={() => setRiskFilter((cur) => cur === "requiring_review" ? "any" : "requiring_review")}
                className={`text-left p-5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  riskFilter === "requiring_review" ? "bg-blue-50 ring-2 ring-blue-400" : "hover:bg-amber-50/40 cursor-pointer"
                }`}
                data-testid="stat-risk-indicators-requiring-review-btn"
                data-active={riskFilter === "requiring_review" ? "true" : "false"}
              >
                <div className="ntn-eyebrow flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                  Requiring Review
                </div>
                <div className="ntn-metric-sm mt-2" data-testid="stat-risk-indicators-requiring-review">{stats.risk_indicators_requiring_review ?? 0}</div>
                <div className="ntn-caption mt-1.5">Open or under review</div>
              </button>
              <div className="p-5">
                <div className="ntn-eyebrow flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Open
                </div>
                <div className="ntn-metric-sm mt-2" data-testid="stat-open-risk-indicators">{stats.open_risk_indicators ?? 0}</div>
                <div className="ntn-caption mt-1.5">Not yet actioned</div>
              </div>
              <div className="p-5">
                <div className="ntn-eyebrow flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Resolved
                </div>
                <div className="ntn-metric-sm mt-2" data-testid="stat-resolved-risk-indicators">{stats.resolved_risk_indicators ?? 0}</div>
                <div className="ntn-caption mt-1.5">Concern addressed</div>
              </div>
              <div className="p-5 hidden lg:block">
                <div className="ntn-eyebrow flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                  Dismissed
                </div>
                <div className="ntn-metric-sm mt-2" data-testid="stat-dismissed-risk-indicators">{stats.dismissed_risk_indicators ?? 0}</div>
                <div className="ntn-caption mt-1.5">No effect on decision</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {riskFilter !== "any" && (
        <div className="mb-5 flex items-center gap-2 ntn-fade-up" data-testid="risk-filter-active">
          <span className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Risk filter</span>
          <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-amber-500 text-white" data-testid="risk-filter-chip">
            <span className="font-medium">
              {riskFilter === "with_indicators" ? "With Risk Indicators" : "Requiring Review"}
            </span>
            <span className="font-mono tabular-nums text-white/80">· {filtered.length}</span>
            <button
              type="button"
              onClick={() => setRiskFilter("any")}
              className="ml-1 h-4 w-4 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center"
              data-testid="risk-filter-clear"
              aria-label="Clear risk filter"
            >
              <CloseIcon className="h-2.5 w-2.5" />
            </button>
          </span>
        </div>
      )}

      {stageFilter && stageFilter !== "all" && (
        <div className="mb-5 flex items-center gap-2 ntn-fade-up" data-testid="stage-filter-active">
          <span className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Showing</span>
          <span
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-slate-900 text-white"
            data-testid="stage-filter-chip"
          >
            <span className="font-medium">{STAGE_FILTER_LABEL[stageFilter] || stageFilter}</span>
            <span className="font-mono tabular-nums text-white/70">· {filtered.length}</span>
            <button
              type="button"
              onClick={() => setStageFilter(null)}
              className="ml-1 h-4 w-4 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              data-testid="stage-filter-clear"
              aria-label="Clear stage filter"
            >
              <CloseIcon className="h-2.5 w-2.5" />
            </button>
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-5 ntn-fade-up" style={{ animationDelay: "0.1s" }}>
        <div className="flex gap-2 items-center flex-wrap">
          {/* "Flat list" view intentionally removed per platform spec:
              applicants must NEVER appear as top-level entries on the dashboard.
              The view state still exists internally for merge-mode admin flows. */}
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="h-9 bg-[#F3EFEA] p-0.5 rounded-sm">
              {[["all","All"],["awaiting","Awaiting"],["open","Open"],["decided","Decided"]].map(([v,l]) => (
                <TabsTrigger key={v} value={v} className="rounded-sm data-[state=active]:bg-white text-xs px-3 h-8" data-testid={`tab-${v}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <button type="button" onClick={() => setShowArchived((v) => { const next = !v; if (!next) setFinalDecisionFilter("all"); return next; })}
            data-testid="toggle-archived"
            className={`h-9 px-3 rounded-sm text-xs font-mono tracking-[0.12em] uppercase border transition-colors ${
              showArchived
                ? "bg-[#0A192F] text-white border-[#0A192F]"
                : "bg-white text-[#4A5568] border-[#0A192F]/15 hover:border-[#0A192F]/30"
            }`}>
            {showArchived ? "Showing archived" : "Show archived"}
          </button>
          {canMerge && (
            <button type="button"
              onClick={() => { if (selectMode) { exitSelectMode(); } else { setSelectMode(true); setView("flat"); } }}
              data-testid="toggle-merge-mode"
              className={`h-9 px-3 rounded-sm text-xs font-mono tracking-[0.12em] uppercase border transition-colors flex items-center gap-1.5 ${
                selectMode
                  ? "bg-[#F59E0B] text-white border-[#F59E0B]"
                  : "bg-white text-[#4A5568] border-[#0A192F]/15 hover:border-[#0A192F]/30"
              }`}>
              <CheckIcon className="h-3.5 w-3.5" />
              {selectMode ? `Cancel (${selectedIds.size})` : "Select to merge / archive"}
            </button>
          )}
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search applicant or property…"
            className="pl-9 h-9 rounded-sm bg-white border-[#0A192F]/15" data-testid="dashboard-search" />
        </div>
        {isStaff && (
          <div className="relative max-w-[220px] w-full">
            <Input value={subscriberFilter} onChange={(e) => setSubscriberFilter(e.target.value)}
              placeholder="Subscriber: All Subscribers"
              className="h-9 rounded-sm bg-white border-[#0A192F]/15" data-testid="dashboard-subscriber-filter" />
          </div>
        )}
      </div>

      {/* Closed Applications Archive — filter by PM Final Decision outcome.
          Only rendered when the archive view is active. The three outcomes
          (Approved / Not Approved / Not Selected) are non-overlapping. */}
      {showArchived && (
        <div className="bg-[#F3EFEA]/50 border border-[#0A192F]/10 rounded-sm px-4 py-3 mb-5 flex items-center gap-3 flex-wrap"
             data-testid="closed-archive-filters">
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Closed Applications · Final Decision
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { value: "all",          label: "All closed",   testid: "final-filter-all" },
              { value: "approved",     label: "Approved",     testid: "final-filter-approved" },
              { value: "denied",       label: "Not Approved", testid: "final-filter-denied" },
              { value: "not_selected", label: "Not Selected", testid: "final-filter-not_selected" },
            ].map((chip) => {
              const active = finalDecisionFilter === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => setFinalDecisionFilter(chip.value)}
                  data-testid={chip.testid}
                  className={`h-7 px-3 rounded-full text-[11px] font-mono tracking-[0.1em] uppercase border transition-colors ${
                    active
                      ? "bg-[#0A192F] text-white border-[#0A192F]"
                      : "bg-white text-[#4A5568] border-[#0A192F]/15 hover:border-[#0A192F]/30"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#0A192F]/10 rounded-sm p-12 text-center ntn-fade-up" data-testid="empty-state">
          <FileText className="h-10 w-10 mx-auto text-[#4A5568]/40 mb-4" />
          <div className="font-serif text-xl text-[#0A192F] mb-2">Nothing here yet</div>
          <p className="text-sm text-[#4A5568]">
            {user?.role === "property_manager"
              ? (hasPermission?.("reports.order")
                  ? "Send your first application to get started."
                  : "No applications are currently visible in your scope.")
              : "No screenings match your filter."}
          </p>
        </div>
      ) : view === "by_property" ? (
        <div data-testid="property-list">
          <div className="flex items-center justify-between mb-4 px-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
                Portfolio
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-600 font-medium" data-testid="property-count-label">
                {byProperty.length} propert{byProperty.length === 1 ? "y" : "ies"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPropertyExpansion(true)}
                data-testid="property-expand-all"
                className="text-[10px] uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-100 px-2.5 py-1 rounded-md transition-colors"
              >
                Expand all
              </button>
              <span className="text-slate-300 text-xs">·</span>
              <button
                type="button"
                onClick={() => setPropertyExpansion(false)}
                data-testid="property-collapse-all"
                className="text-[10px] uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-100 px-2.5 py-1 rounded-md transition-colors"
              >
                Collapse all
              </button>
            </div>
          </div>
          <div className="space-y-5 ntn-stagger">
            {byProperty.map(({ address, list }, i) => (
              <PropertyCard
                key={address}
                address={address}
                screenings={list}
                defaultOpen={propertyDefaultOpen ?? (i < 3)}
                expansionNonce={expansionNonce}
                selectable={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onArchiveToggle={rowArchiveToggle}
                groupsById={groupsById}
                onArchiveGroup={groupArchiveToggle}
                unreadByScreening={unreadByScreening}
                unreadByGroup={unreadByGroup}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#0A192F]/10 rounded-sm overflow-hidden ntn-fade-up">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#F3EFEA]/60 border-b border-[#0A192F]/10 font-overline text-[10px] text-[#4A5568]">
            {selectMode && <div className="col-span-1">Select</div>}
            <div className={selectMode ? "col-span-2" : "col-span-3"}>Applicant</div>
            <div className="col-span-3">Property</div>
            <div className="col-span-2">Reports</div>
            <div className="col-span-2">Submitted</div>
            <div className="col-span-2 text-right">Decision</div>
          </div>
          {filtered.map((s) => (
            <ScreeningRow key={s.id} s={s}
              selectable={selectMode}
              selected={selectedIds.has(s.id)}
              onToggleSelect={toggleSelect}
              onArchiveToggle={rowArchiveToggle}
              unreadTypes={unreadByScreening[s.id]} />
          ))}
        </div>
      )}

      {selectMode && (
        <MergeStickyBar
          count={selectedScreenings.length}
          archivedCount={selectedScreenings.filter((s) => !!s.archived_at).length}
          isStaff={isStaff}
          onCancel={exitSelectMode}
          onMerge={() => setMergeOpen(true)}
          onBulkArchive={bulkArchive}
          onBulkRestore={bulkRestore}
          onBulkPermanentDelete={bulkPermanentDelete}
        />
      )}

      <MergeDialog
        open={mergeOpen}
        onOpenChange={(v) => setMergeOpen(v)}
        screenings={selectedScreenings}
        onMerged={(gid) => {
          setMergeOpen(false);
          exitSelectMode();
          reload();
          toast.success("Applicants merged into a household");
          if (gid) navigate(`/groups/${gid}`);
        }}
      />
    </AppShell>
  );
}


/* ---------------- Household Risk Widget ---------------- */
const VERDICT_CFG = {
  approve:     { tone: "#22C55E", label: "APPROVE",     Icon: CheckCircle2 },
  conditional: { tone: "#F59E0B", label: "CONDITIONAL", Icon: AlertTriangle },
  deny:        { tone: "#EF4444", label: "DENY",        Icon: XCircle },
  pending:     { tone: "#4A5568", label: "PENDING",     Icon: MinusCircle },
};

function HouseholdRiskWidget({ groups, onArchive }) {
  // Sort: actionable verdicts first (deny → conditional → approve → pending), then newest
  const sorted = useMemo(() => {
    const order = { deny: 0, conditional: 1, approve: 2, pending: 3 };
    return [...groups].sort((a, b) => {
      const av = a.recommendation || a.household?.suggested_verdict || "pending";
      const bv = b.recommendation || b.household?.suggested_verdict || "pending";
      if (order[av] !== order[bv]) return order[av] - order[bv];
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [groups]);

  const summary = useMemo(() => {
    const out = { total: groups.length, approve: 0, conditional: 0, deny: 0, pending: 0 };
    groups.forEach((g) => {
      const v = g.recommendation || g.household?.suggested_verdict || "pending";
      out[v] = (out[v] || 0) + 1;
    });
    return out;
  }, [groups]);

  return (
    <section className="mb-8 ntn-fade-up" data-testid="household-risk-widget">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#0A192F]" />
            <h2 className="font-serif text-2xl text-[#0A192F]">Household Risk</h2>
          </div>
          <p className="text-xs text-[#4A5568] mt-1">{summary.total} group{summary.total !== 1 ? "s" : ""} · {summary.deny} deny · {summary.conditional} conditional · {summary.approve} approve · {summary.pending} pending</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {sorted.slice(0, 6).map((g) => <HouseholdCardMini key={g.id} g={g} onArchive={onArchive} />)}
      </div>
    </section>
  );
}

function HouseholdCardMini({ g, onArchive }) {
  const h = g.household || {};
  const verdict = g.recommendation || h.suggested_verdict || "pending";
  const cfg = VERDICT_CFG[verdict];
  const dp = h.dp_score;
  const pct = dp != null ? Math.max(0, Math.min(100, dp)) : 0;
  const dpTone = pct >= 75 ? "#22C55E" : pct >= 50 ? "#F59E0B" : "#EF4444";

  const activeMembers = (g.member_screenings || []).filter((m) => !m.removed);
  const reportsDone = activeMembers.filter(
    (m) => (m.workflow_stage || m.verify_status || m.overall_status) === "complete",
  ).length;
  const reportsTotal = activeMembers.length;
  const archived = !!g.archived_at;

  return (
    <Link to={`/groups/${g.id}`} data-testid={`household-card-${g.id}`}
      className={`block bg-white border rounded-sm p-4 ntn-card-hover transition-colors group relative ${
        archived ? "border-[#0A192F]/10 opacity-70" : "border-[#0A192F]/10 hover:border-[#0A192F]/30"
      }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-serif text-base text-[#0A192F] truncate group-hover:underline decoration-1 underline-offset-4">{g.name}</div>
          <div className="text-[11px] text-[#4A5568] font-mono truncate">{g.property_address}</div>
          <div className="mt-1.5">
            <StageBadge group={g} size="xs" showIcon={false} testid={`household-stage-${g.id}`} />
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold tracking-[0.14em] px-2 py-1 rounded-sm text-white"
               style={{ background: cfg.tone }}>
            <cfg.Icon className="h-3 w-3" strokeWidth={2.5} />
            {cfg.label}
          </div>
          {onArchive && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onArchive(g); }}
              title={archived ? "Restore household" : "Archive household"}
              data-testid={`household-archive-${g.id}`}
              className={`h-7 w-7 rounded-sm flex items-center justify-center transition-all
                ${archived
                  ? "text-[#15803D] hover:bg-[#22C55E]/15"
                  : "text-[#4A5568] opacity-0 group-hover:opacity-100 hover:bg-[#F3EFEA] hover:text-[#0A192F]"}`}
            >
              {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Mini metrics row: DP arc + members + FICO list + RTI */}
      <div className="grid grid-cols-12 gap-3 items-center">
        {/* Mini DP gauge */}
        <div className="col-span-4">
          <svg viewBox="0 0 100 60" className="w-full">
            <path d="M 8 50 A 42 42 0 0 1 92 50" fill="none" stroke="#E5E0D8" strokeWidth="8" strokeLinecap="round" />
            {dp != null && (
              <path d="M 8 50 A 42 42 0 0 1 92 50" fill="none" stroke={dpTone} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={Math.PI * 42}
                strokeDashoffset={(1 - pct / 100) * Math.PI * 42} />
            )}
            <text x="50" y="42" textAnchor="middle" fontFamily="Georgia, serif" fontSize="22" fill="#0A192F" fontWeight="600">
              {dp != null ? dp : "—"}
            </text>
          </svg>
          <div className="text-[9px] font-mono tracking-[0.14em] text-[#4A5568] text-center -mt-1">DP AVG</div>
        </div>

        {/* Side metrics */}
        <div className="col-span-8 space-y-1.5">
          <MiniRow label={`${g.member_screenings?.length || 0} APPLICANTS`}
                   value={(g.member_screenings || []).map((m) => m.role === "primary" ? "P" : m.role === "co_applicant" ? "C" : "S").join(" · ")} />
          <MiniRow label="FICO" value={
            (h.fico_breakdown || []).length === 0
              ? "—"
              : (h.fico_breakdown || []).map((f) =>
                  f.fico_status === "not_available" ? "N/A"
                  : (f.fico_score === -9999 || f.fico_status === "insufficient_history") ? "Insuff."
                  : f.fico_score ?? "—").join(" · ")
          } />
          <MiniRow label="RTI" value={h.rent_to_income_ratio != null ? `${h.rent_to_income_ratio}%` : "—"}
            valueTone={h.rent_to_income_ratio == null ? "" :
              h.rent_to_income_ratio <= 30 ? "text-[#15803D]" :
              h.rent_to_income_ratio <= 40 ? "text-[#92400E]" : "text-[#B91C1C]"} />
        </div>
      </div>

      {/* Risk-indicator row */}
      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[#0A192F]/10 flex-wrap">
        {h.eviction_records_found && <MiniFlag label="EVICTION" />}
        {h.criminal_records_found && <MiniFlag label="CRIMINAL" />}
        {(h.document_validation === "invalid" || h.document_validation === "fraud_suspected") && <MiniFlag label="DOC INVALID" />}
        {h.employment_verified === "unable" && <MiniFlag label="UNVERIFIED EMP" tone="warn" />}
        {h.landlord_verified === "unable" && <MiniFlag label="UNVERIFIED LANDLORD" tone="warn" />}
        {!h.eviction_records_found && !h.criminal_records_found &&
          h.document_validation !== "invalid" && h.document_validation !== "fraud_suspected" &&
          h.employment_verified !== "unable" &&
          h.landlord_verified !== "unable" && (
          <span className="text-[9px] font-mono tracking-[0.14em] text-[#4A5568] uppercase">No risk indicators</span>
        )}
        <span className="ml-auto text-[9px] font-mono tracking-[0.14em] text-[#4A5568] uppercase">
          {reportsDone}/{reportsTotal} REPORTS COMPLETE
        </span>
      </div>
    </Link>
  );
}

function MiniRow({ label, value, valueTone = "text-[#0A192F]" }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="font-mono tracking-[0.12em] text-[#4A5568] uppercase text-[9px]">{label}</span>
      <span className={`font-mono truncate ${valueTone}`}>{value}</span>
    </div>
  );
}

function MiniFlag({ label, tone = "bad" }) {
  const p = tone === "warn"
    ? "bg-[#F59E0B]/15 text-[#92400E]"
    : "bg-[#EF4444]/15 text-[#B91C1C]";
  return (
    <span className={`text-[9px] font-mono font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm ${p}`}>
      {label}
    </span>
  );
}


/* ---------------- Merge UI ---------------- */
function MergeStickyBar({ count, archivedCount, isStaff, onCancel, onMerge, onBulkArchive, onBulkRestore, onBulkPermanentDelete }) {
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 ntn-fade-up" data-testid="merge-sticky-bar">
      <div className="bg-[#0A192F] text-white rounded-sm shadow-2xl px-5 py-3 flex items-center gap-3 border border-white/10 flex-wrap">
        <div className="text-sm">
          <span className="font-serif text-lg">{count}</span>{" "}
          <span className="text-white/70">applicant{count !== 1 ? "s" : ""} selected</span>
        </div>
        <button type="button" onClick={onCancel}
          className="text-xs font-mono tracking-[0.12em] uppercase text-white/60 hover:text-white"
          data-testid="merge-bar-cancel">
          <CloseIcon className="h-3.5 w-3.5 inline mr-1" /> Cancel
        </button>
        <div className="h-6 w-px bg-white/15 mx-1" />
        <Button onClick={onBulkArchive} disabled={count === 0} variant="outline"
          className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A192F] rounded-sm"
          data-testid="bulk-archive-confirm">
          <Archive className="h-4 w-4 mr-2" /> Archive
        </Button>
        {isStaff && archivedCount > 0 && (
          <Button onClick={onBulkRestore} variant="outline"
            className="bg-transparent border-emerald-300/60 text-emerald-200 hover:bg-emerald-500 hover:text-white rounded-sm"
            data-testid="bulk-restore-confirm">
            <ArchiveRestore className="h-4 w-4 mr-2" /> Restore ({archivedCount})
          </Button>
        )}
        <Button onClick={onMerge} disabled={count < 2}
          className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0A192F] rounded-sm font-semibold"
          data-testid="merge-bar-confirm">
          <MergeIcon className="h-4 w-4 mr-2" />
          {count < 2 ? "Select 2+ to merge" : "Merge into household"}
        </Button>
        {isStaff && (
          <>
            <Dialog open={delOpen} onOpenChange={(v) => { setDelOpen(v); if (!v) { setDelReason(""); setDelConfirm(""); } }}>
              <DialogTrigger asChild>
                <Button variant="outline"
                  className="bg-transparent border-red-300/60 text-red-200 hover:bg-red-600 hover:text-white rounded-sm"
                  data-testid="bulk-permanent-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-slate-900">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" /> Delete {count} applicant{count !== 1 ? "s" : ""} permanently?
                  </DialogTitle>
                  <DialogDescription>
                    Removes the applicant records and every related artifact (files, notes, activity, messages, queue assignments). Each deletion is logged in the audit log. <strong>This cannot be undone.</strong>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Reason (required)</div>
                    <textarea
                      rows={3} maxLength={1000}
                      value={delReason}
                      onChange={(e) => setDelReason(e.target.value)}
                      placeholder="Why are these records being removed?"
                      className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      data-testid="bulk-delete-reason"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Type <span className="font-mono text-red-700">DELETE</span> to confirm</div>
                    <Input
                      value={delConfirm}
                      onChange={(e) => setDelConfirm(e.target.value.toUpperCase())}
                      className="mt-1.5 rounded-md font-mono"
                      data-testid="bulk-delete-confirm"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
                  <Button
                    disabled={delConfirm !== "DELETE" || delReason.trim().length < 3}
                    onClick={() => { onBulkPermanentDelete(delReason.trim()); setDelOpen(false); setDelReason(""); setDelConfirm(""); }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="bulk-delete-confirm-btn"
                  >
                    Delete permanently
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}

function MergeDialog({ open, onOpenChange, screenings, onMerged }) {
  const [primaryId, setPrimaryId] = useState("");
  const [roles, setRoles] = useState({});
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && screenings.length > 0) {
      const first = screenings[0];
      setPrimaryId(first.id);
      const initial = {};
      screenings.forEach((s) => { if (s.id !== first.id) initial[s.id] = "co_applicant"; });
      setRoles(initial);
      setName("");
    }
  }, [open, screenings]);

  const setRole = (sid, role) => setRoles((r) => ({ ...r, [sid]: role }));

  const submit = async () => {
    if (!primaryId) return;
    setSubmitting(true);
    try {
      const additional = screenings
        .filter((s) => s.id !== primaryId)
        .map((s) => ({ screening_id: s.id, role: roles[s.id] || "co_applicant" }));
      const payload = { primary_screening_id: primaryId, additional };
      if (name.trim()) payload.name = name.trim();
      const { data } = await apiClient.post("/groups/merge", payload);
      onMerged?.(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Merge failed");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  const primary = screenings.find((s) => s.id === primaryId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <MergeIcon className="h-5 w-5" /> Merge into a household
          </DialogTitle>
          <DialogDescription>
            One household = one decision. Pick the primary applicant; everyone else becomes a co-applicant or cosigner.
            The household inherits the primary's property and reports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="font-overline text-[#4A5568] mb-2">Primary applicant</div>
            <Select value={primaryId} onValueChange={setPrimaryId}>
              <SelectTrigger className="h-11 rounded-sm" data-testid="merge-primary-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {screenings.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`merge-primary-option-${s.id}`}>
                    {s.applicant_first_name} {s.applicant_last_name} · {s.property_address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {primary && (
              <p className="text-[11px] text-[#4A5568] mt-2 font-mono">
                HOUSEHOLD WILL USE: {primary.property_address.toUpperCase()}{primary.monthly_rent ? ` · $${Number(primary.monthly_rent).toLocaleString()}/MO` : ""}
              </p>
            )}
          </div>

          <div>
            <div className="font-overline text-[#4A5568] mb-2">Other applicants</div>
            <ul className="border border-[#0A192F]/10 rounded-sm divide-y divide-[#0A192F]/8">
              {screenings.filter((s) => s.id !== primaryId).map((s) => (
                <li key={s.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`merge-other-${s.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-[#0A192F] truncate">{s.applicant_first_name} {s.applicant_last_name}</div>
                    <div className="text-[11px] text-[#4A5568] font-mono truncate">{s.applicant_email}</div>
                  </div>
                  <Select value={roles[s.id] || "co_applicant"} onValueChange={(v) => setRole(s.id, v)}>
                    <SelectTrigger className="h-9 rounded-sm w-40" data-testid={`merge-role-${s.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="co_applicant">Co-applicant</SelectItem>
                      <SelectItem value="cosigner">Cosigner</SelectItem>
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-overline text-[#4A5568] mb-2">Household name (optional)</div>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder={primary ? `${primary.applicant_last_name} household · ${primary.property_address}` : "Smith household · 1407 Lakeview Dr"}
              className="h-11 rounded-sm" data-testid="merge-name" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !primaryId}
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="merge-dialog-confirm">
            {submitting ? "Merging…" : <><CheckIcon className="h-4 w-4 mr-2" /> Create household</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
